// app/javascript/controllers/floating_toc_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["item", "link", "indicator", "scrollContainer"]

  connect() {
    this.setupScrollListener()
    this.initializeFallbackMapping()
    this.updateActiveSection()
  }

  disconnect() {
    // Remove scroll listener
    window.removeEventListener('scroll', this.boundUpdateActiveSection)
  }

  setupScrollListener() {
    this.boundUpdateActiveSection = this.updateActiveSection.bind(this)
    window.addEventListener('scroll', this.boundUpdateActiveSection)
  }

  /**
   * Initialize fallback mapping for TOC items to H2 headings.
   * This creates a mapping by order when UUID-based IDs don't match.
   * Includes rich_text H2s and FAQ sections in order of appearance.
   */
  initializeFallbackMapping() {
    // Find all H2 headings in rich_text components (they have id="rich-text-heading-...")
    // and FAQ sections (they have id="faq-heading-...")
    const allH2s = Array.from(document.querySelectorAll('h2[id^="rich-text-heading-"], h2[id^="faq-heading-"]'))
    
    // Store the fallback mapping: TOC index -> H2 element
    this.fallbackMapping = {}
    
    this.itemTargets.forEach((item, index) => {
      const targetId = item.dataset.itemId
      const element = document.getElementById(targetId)
      
      if (!element && allH2s[index]) {
        // If the intended element doesn't exist, map this TOC item to the H2 at the same index
        this.fallbackMapping[index] = allH2s[index]
        console.log(`TOC Fallback: Item ${index} ("${item.textContent.trim()}") -> H2 "${allH2s[index].textContent.trim()}"`)
      }
    })
    
    if (Object.keys(this.fallbackMapping).length > 0) {
      console.warn(`TOC: Using fallback mapping for ${Object.keys(this.fallbackMapping).length} items due to UUID mismatch`)
    }
  }

  /**
   * Get the target element for a TOC item, using fallback if needed.
   * @param {number} index - The TOC item index
   * @param {string} targetId - The intended target ID
   * @returns {HTMLElement|null} - The target element
   */
  getTargetElement(index, targetId) {
    const element = document.getElementById(targetId)
    if (element) {
      return element
    }
    
    // Use fallback mapping if available
    if (this.fallbackMapping[index]) {
      return this.fallbackMapping[index]
    }
    
    return null
  }

  updateActiveSection() {
    // Calculate scroll position
    const scrollPosition = window.scrollY + 125
    
    // Get all sections using both direct ID lookup and fallback mapping
    const sections = this.itemTargets.map((item, index) => {
      const targetId = item.dataset.itemId
      return this.getTargetElement(index, targetId)
    }).filter(Boolean)
    
    let activeElement = null
    
    // Find the current section
    for (let i = 0; i < sections.length; i++) {
      const section = sections[i]
      const nextSection = sections[i + 1]
      
      const sectionTop = section.offsetTop
      const sectionBottom = nextSection ? nextSection.offsetTop : document.documentElement.scrollHeight
      
      if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
        activeElement = section
        break
      }
    }
    
    // Update active states
    this.updateActiveState(activeElement)
  }

  updateActiveState(activeElement) {
    // Reset all items
    this.itemTargets.forEach((item, index) => {
      const link = this.linkTargets[index]
      const indicator = this.indicatorTargets[index]
      
      // Remove active styling
      link.classList.remove("font-semibold", "text-ph24_blue")
      link.classList.add("text-gray-600")
      
      if (indicator) {
        indicator.style.backgroundColor = "transparent"
      }
    })
    
    // Apply active styling only if there's an active section
    if (activeElement) {
      // Find which TOC item corresponds to this element
      const activeIndex = this.itemTargets.findIndex((item, index) => {
        const targetId = item.dataset.itemId
        const element = this.getTargetElement(index, targetId)
        return element === activeElement
      })
      
      if (activeIndex >= 0) {
        const activeLink = this.linkTargets[activeIndex]
        const activeIndicator = this.indicatorTargets[activeIndex]
        const activeItem = this.itemTargets[activeIndex]
        
        // Add active styling
        activeLink.classList.remove("text-gray-600")
        activeLink.classList.add("font-semibold", "text-ph24_blue")
        
        if (activeIndicator) {
          activeIndicator.style.backgroundColor = "#12273e" // ph24_blue
        }
        
        // Scroll the active item into view within the TOC container
        this.scrollActiveItemIntoView(activeItem)
      }
    }
  }
  
  /**
   * Scroll the active TOC item into view within its scrollable container
   * @param {HTMLElement} activeItem - The active TOC list item
   */
  scrollActiveItemIntoView(activeItem) {
    if (!this.hasScrollContainerTarget) return
    
    const container = this.scrollContainerTarget
    const containerRect = container.getBoundingClientRect()
    const itemRect = activeItem.getBoundingClientRect()
    
    // Calculate if item is outside the visible area of the container
    const isAboveView = itemRect.top < containerRect.top
    const isBelowView = itemRect.bottom > containerRect.bottom
    
    if (isAboveView || isBelowView) {
      // Calculate the ideal scroll position to center the item in the container
      const containerHeight = containerRect.height
      const itemHeight = itemRect.height
      const itemOffsetInContainer = activeItem.offsetTop
      
      // Center the item vertically in the container
      const idealScrollTop = itemOffsetInContainer - (containerHeight / 2) + (itemHeight / 2)
      
      // Smooth scroll to the position
      container.scrollTo({
        top: idealScrollTop,
        behavior: 'smooth'
      })
    }
  }

  scrollToSection(event) {
    event.preventDefault()
    const href = event.currentTarget.getAttribute("href")
    const id = href.substring(1)
    
    // First try direct ID lookup
    let element = document.getElementById(id)
    
    // If not found, use fallback mapping
    if (!element) {
      const tocIndex = this.linkTargets.findIndex(link => link.getAttribute("href") === href)
      if (tocIndex >= 0 && this.fallbackMapping[tocIndex]) {
        element = this.fallbackMapping[tocIndex]
        console.log(`TOC: Using fallback for scroll to "${this.itemTargets[tocIndex].textContent.trim()}"`)
      }
    }
    
    if (element) {
      const yOffset = -125
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset
      window.scrollTo({ top: y, behavior: 'smooth' })
    }
  }
}